/**
 * Add-players panel container (SE-5490, redesigned by SE-6945 PC1–PC7).
 *
 * Owns the drawer's state: open/closed, the selected source tab, the search
 * text, the GPR sort, the committed filter state (advanced groups + the
 * REASON selection) and whether the in-drawer filters panel is showing. The
 * body the drawer renders is DERIVED from that state, in precedence order:
 * the filters panel (PC3/PC4), else active-search results (PC2), else
 * filtered results when any filter is committed (PC7), else the tabbed pools
 * (PC1/PC5/PC6). Clearing the search or the last filter therefore restores
 * the previous tab state by construction — the tab selection was never
 * touched.
 *
 * SE-6554 turned the panel from a left rail into a right-anchored overlay
 * drawer (Figma 80:15673); SE-6945 applies the Scouting drawer design
 * (Figma PC1 362:39276).
 *
 * ## Open and tab defaults are MOUNT defaults, not effects
 *
 * Both defaults are properties of the PIPELINE — the card count that decides
 * open/closed, and the Suggested tab the panel opens on — so both have to be
 * re-evaluated when the user switches pipelines. Resetting them with an
 * effect is banned by `react-hooks/set-state-in-effect`, so the reset is a
 * KEY-RESET: `AddPlayersPanelContainer` at the foot of this file renders the
 * stateful component with `key={kanbanBoardId}`, and React remounts it with
 * fresh defaults on a pipeline switch. That also means adding a card to the OPEN
 * pipeline does NOT close the drawer mid-use — the default is evaluated once
 * per pipeline, which is what "closed by default" means.
 * @module features/player-kanban/components/organisms/AddPlayersPanel
 */

import { useDroppable } from "@dnd-kit/core";
import React, { useCallback, useMemo, useState } from "react";

import type {
  KanbanPipelineTemplate,
  SuggestionReasonCategory,
} from "@/generated/graphql";
import useFilterState from "@/hooks/useFilterState";
import useMobileScreen from "@/hooks/useMobileScreen";

import type { FilterState } from "../../../constants/filterDefaults";
import type { ServerGpvRange } from "../../../utils/playerFilter";
import { SUGGESTION_REASON_COPY } from "../../../constants/suggestionReasons";
import { useAddPlayersPanelState } from "../../../hooks/useAddPlayersPanelState";
import useAddPlayersSearch from "../../../hooks/useAddPlayersSearch";
import useAddPlayersTabCounts from "../../../hooks/useAddPlayersTabCounts";
import useAddSuggestedPlayer from "../../../hooks/useAddSuggestedPlayer";
import useSuggestedPlayers from "../../../hooks/useSuggestedPlayers";
import type { RemovePlayerHandler } from "../../../types/types";
import useWatchlistFilterOptions from "../../../hooks/useWatchlistFilterOptions";
import { type ReasonSelection } from "../../../utils/addPlayersDrawerControls";
import {
  DEFAULT_ADD_PLAYERS_SOURCE,
  resolveAddPlayersSources,
  resolveSelectedSource,
  type AddPlayersSource,
} from "../../../utils/addPlayersPanelSources";
import AddPlayersFilterPanel, {
  type ReasonOption,
} from "../AddPlayersFilterPanel";
import AddPlayersFilteredResults from "../AddPlayersFilteredResults";
import AddPlayersSourceList from "../AddPlayersSourceList";
import SuggestedPlayersList from "../SuggestedPlayersList";

import AddPlayersPanelView, {
  type AddPlayersBodyMode,
} from "./AddPlayersPanelView";

/** Props for the AddPlayersPanel container. */
export interface AddPlayersPanelContainerProps {
  /** The open pipeline. */
  readonly kanbanBoardId: string | null | undefined;
  /** The pipeline's display name, for the "ADD PLAYERS TO" header (PC1). */
  readonly boardName?: string | null;
  /**
   * The pipeline's recruitment motion, including the `null`/`undefined` of a
   * MIGRATED board. Every value offers every source (SE-6913).
   */
  readonly pipelineTemplate?: KanbanPipelineTemplate | null;
  /** Cards already on the board. Drives the collapse default and placed markers. */
  readonly kanbanCards?: {
    player: { id: string };
    kanbanPhase: { id: string; name: string };
  }[];
  /** Whether any drag is in progress. */
  readonly isGloballyDragging?: boolean;
  /** Where the current drag started. */
  readonly dragSource?: "sidebar" | "kanban" | null;
  /** Active board filters. */
  readonly boardFilters?: FilterState;
  /**
   * Active GPV bounds (euros) for the server-side watchlist filter (SE-6496).
   * Null while the cost filter is inactive.
   */
  readonly serverGpvRange?: ServerGpvRange | null;
  /** Drag start callback. */
  readonly onGlobalDragStart?: (dragData?: unknown, event?: unknown) => void;
  /** Drag end callback. */
  readonly onGlobalDragEnd?: () => void;
  /** Remove-from-board handler. */
  readonly onRemovePlayerFromBoard?: RemovePlayerHandler;
  /** Auto-scroll callback. */
  readonly onAutoScroll?: (dragX: number) => void;
}

/**
 * Internal props for the stateful panel — the public props plus the
 * key-reset wrapper's SE-6488 expand seed.
 */
interface AddPlayersPanelStatefulProps extends AddPlayersPanelContainerProps {
  /**
   * Mount the rail expanded, overriding the card-count default. Set by the
   * wrapper when the board header's `+ Add player` CTA has requested expansion
   * for this board.
   */
  readonly startExpanded?: boolean;
}

/**
 * Drive the add-players panel.
 * @param root0 - Component props.
 * @param root0.kanbanBoardId - The open pipeline.
 * @param root0.boardName - The pipeline's display name (PC1 header).
 * @param root0.pipelineTemplate - The pipeline's motion.
 * @param root0.kanbanCards - Cards already on the board.
 * @param root0.isGloballyDragging - Whether a drag is in progress.
 * @param root0.dragSource - Where the drag started.
 * @param root0.boardFilters - Active board filters.
 * @param root0.serverGpvRange - Active GPV bounds for the server-side watchlist filter (SE-6496).
 * @param root0.onGlobalDragStart - Drag start callback.
 * @param root0.onGlobalDragEnd - Drag end callback.
 * @param root0.onRemovePlayerFromBoard - Remove handler.
 * @param root0.onAutoScroll - Auto-scroll callback.
 * @param root0.startExpanded - Mount expanded, overriding the card-count default (SE-6488).
 */
const AddPlayersPanelStateful = ({
  kanbanBoardId,
  boardName,
  pipelineTemplate,
  kanbanCards = [],
  isGloballyDragging = false,
  dragSource = null,
  boardFilters,
  serverGpvRange = null,
  onGlobalDragStart,
  onGlobalDragEnd,
  onRemovePlayerFromBoard,
  onAutoScroll,
  startExpanded = false,
}: AddPlayersPanelStatefulProps) => {
  /*
   * The panel never opens itself; only an explicit `startExpanded` does.
   *
   * It used to open on an EMPTY pipeline (SE-5490), which made sense while the
   * panel was an inline RAIL: with nothing on the board to read, it cost the
   * user nothing to have the rail already showing, and it yielded the width
   * back once cards existed. SE-6554 turned the rail into a right-hand overlay
   * drawer, and that container does not yield width — it covers the board
   * behind a full-viewport backdrop that swallows pointer events. So the same
   * default now hijacks the empty board it was meant to help: every control is
   * unreachable, including the pipeline chip row's "+ New", the only entry
   * point for creating a pipeline (proven by pipeline-create-entry-point.spec).
   *
   * Product decision 2026-08-08 (Raniel Silva): do not auto-open. The panel
   * stays one click away on the Add Players button.
   */
  const [isOpen, setIsOpen] = useState(() => startExpanded);
  const [requestedSource, setRequestedSource] = useState<AddPlayersSource>(
    DEFAULT_ADD_PLAYERS_SOURCE
  );
  const isMobile = useMobileScreen();

  /*
   * ## The drawer's filters live HERE (SE-6949)
   *
   * `filterState` is the COMMITTED advanced filter state (Basic · Financial ·
   * Fit & Ratings · Performance · Goalkeeper); `appliedReasons` is the
   * committed REASON selection. The filters panel drafts against these and
   * commits on Show results; the filtered-results body and the removable
   * chips both read them directly, so removing a chip re-runs the results
   * with no extra wiring. Non-empty committed filters are what routes the
   * drawer to the filtered-results view (PC7) — the tab strip hides, and it
   * comes back the moment the applied set empties (CLEAR ALL, or removing
   * the last chip).
   */
  const filterState = useFilterState();
  const [appliedReasons, setAppliedReasons] = useState<ReasonSelection>([]);
  /* SE-7094: the committed LISTS selection — watchlist ids. */
  const [appliedListIds, setAppliedListIds] = useState<readonly string[]>([]);
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);

  const sources = useMemo(
    () => resolveAddPlayersSources(pipelineTemplate),
    [pipelineTemplate]
  );
  const selectedSource = resolveSelectedSource(requestedSource, sources);

  const search = useAddPlayersSearch(isOpen);
  const { addPlayer, addingPlayerIds } = useAddSuggestedPlayer(kanbanBoardId);

  /*
   * The panel reads the suggestions itself so it can derive the Suggested
   * tab's count and the filters panel's REASON options. This is the SAME
   * query the Suggested body runs, with the same variables — Apollo's default
   * request deduplication collapses the two subscribers onto one network
   * read, and both then observe one normalized cache entry, so the count and
   * the list cannot disagree.
   *
   * Skipped while the drawer is CLOSED so a board that never opens the drawer
   * fires no suggestions read. That mirrors today's profile exactly: the body
   * is unmounted while closed, so the query already only ran on open.
   */
  const suggestions = useSuggestedPlayers(kanbanBoardId, !isOpen);

  /*
   * The org's watchlists — the id set both pool reads (the Watchlists tab and
   * the count query) resolve to. Read through the side-effect-free hook: the
   * drawer must not write `watchListSelectedVar` (shared with the scouting
   * surfaces).
   */
  const watchlistOptionsQuery = useWatchlistFilterOptions(!isOpen);
  const allWatchlistIds = useMemo(
    () => watchlistOptionsQuery.watchlists.map(watchlist => watchlist.id),
    [watchlistOptionsQuery.watchlists]
  );

  /*
   * Live pool totals for the Watchlists / My Roster tabs (PC1). The id
   * list's own `isLoadedSuccessfully` rides along so the hook can tell
   * "confirmed zero watchlists" apart from "not loaded yet, or errored"
   * (SE-7096) — all three read as an empty `allWatchlistIds` array, but
   * only a SUCCESSFUL load is a knowable total of 0 (a network error is not
   * evidence the org has no watchlists).
   */
  const poolCounts = useAddPlayersTabCounts(
    isOpen,
    allWatchlistIds,
    serverGpvRange,
    watchlistOptionsQuery.isLoadedSuccessfully
  );

  /** Players already on this pipeline, for "In list" muting (PC2/PC5/PC7). */
  const pipelinePlayerIds = useMemo(
    () => new Set(kanbanCards.map(card => card.player.id)),
    [kanbanCards]
  );

  const handleAdd = useCallback(
    (playerId: string, reasons?: readonly SuggestionReasonCategory[]) => {
      // SE-5508: `reasons` is supplied only by the Suggested tab (the search
      // tabs have no candidate behind the row) and is used purely for
      // `suggestion_acted.reason_categories`. The single re-entry guard still
      // lives in `useAddSuggestedPlayer`, shared by both tabs.
      void addPlayer(playerId, reasons);
    },
    [addPlayer]
  );

  const handleClose = useCallback(() => setIsOpen(false), []);

  /** Pre-built per-tab handlers so the View never creates inline functions. */
  const sourceHandlers = useMemo(
    () =>
      sources.reduce(
        (acc, source) => ({
          ...acc,
          [source]: () => setRequestedSource(source),
        }),
        {} as Partial<Record<AddPlayersSource, () => void>>
      ),
    [sources]
  );

  const handleFiltersOpen = useCallback(() => setIsFilterPanelOpen(true), []);
  const handleFiltersClose = useCallback(() => setIsFilterPanelOpen(false), []);

  const handleCommitReasons = useCallback(
    (selection: ReasonSelection) => setAppliedReasons(selection),
    []
  );
  const handleRemoveReason = useCallback(
    (category: SuggestionReasonCategory) =>
      setAppliedReasons(current => current.filter(entry => entry !== category)),
    []
  );
  const handleCommitLists = useCallback(
    (selection: readonly string[]) => setAppliedListIds(selection),
    []
  );
  const handleRemoveList = useCallback(
    (listId: string) =>
      setAppliedListIds(current => current.filter(entry => entry !== listId)),
    []
  );
  const { clearAllFilters } = filterState;
  const handleClearAllFilters = useCallback(() => {
    clearAllFilters();
    setAppliedReasons([]);
    setAppliedListIds([]);
  }, [clearAllFilters]);

  /*
   * ## The REASON options are derived from the DATA, deliberately
   *
   * The PRD names five reasons and the Figma chips agree with it. Our
   * schema's `SuggestionReasonCategory` has three, and they are DISJOINT from
   * that list. A hardcoded option list would either offer filters that match
   * nothing or hide the reasons the cards actually show, so the options are
   * the categories the loaded candidates carry (that have copy). The
   * vocabulary gap is real and is flagged for product, not papered over.
   */
  const reasonOptions = useMemo<readonly ReasonOption[]>(() => {
    const present = [
      ...new Set(
        suggestions.candidates.flatMap(candidate => candidate.reasons)
      ),
    ];
    return present.flatMap(category => {
      const copy = SUGGESTION_REASON_COPY[category];
      return copy === undefined ? [] : [{ category, label: copy.label }];
    });
  }, [suggestions.candidates]);

  /*
   * ## Tab counts are live on all three tabs (PC1)
   *
   * Suggested is the loaded candidate count — the same set the body renders.
   * Watchlists / My Roster are the pools' server-side `totalCount`s, read by
   * `useAddPlayersTabCounts` with variables identical to the tab bodies so
   * Apollo collapses the reads. Each count is omitted (bare label) until its
   * total is actually known, rather than flashing a 0.
   */
  const tabCounts = useMemo<Partial<Record<AddPlayersSource, number>>>(
    () => ({
      ...(suggestions.isLoading
        ? {}
        : { suggested: suggestions.candidates.length }),
      ...(poolCounts.watchlist === undefined
        ? {}
        : { watchlist: poolCounts.watchlist }),
      ...(poolCounts.roster === undefined ? {} : { roster: poolCounts.roster }),
    }),
    [
      poolCounts.roster,
      poolCounts.watchlist,
      suggestions.candidates.length,
      suggestions.isLoading,
    ]
  );

  const hasAppliedFilters =
    filterState.activeFilterCount > 0 ||
    appliedReasons.length > 0 ||
    appliedListIds.length > 0;

  /*
   * The drawer body, in precedence order: the filters panel owns the drawer
   * while it is open; an active search owns it next (typing hides the tabs,
   * PC2); committed filters own it after that (PC7); otherwise the tabs.
   */
  const bodyMode: AddPlayersBodyMode = isFilterPanelOpen
    ? "filters"
    : search.isSearching
      ? "search"
      : hasAppliedFilters
        ? "filtered"
        : "tabs";

  // A card dragged OFF the board turns the panel's slot into the removal
  // target — the same behaviour the legacy sidebar has. It is independent of
  // the drawer's open state: the drag happens on the board, with the drawer
  // almost always closed, so the removal zone is its own overlay rather than
  // drawer content.
  const showRemovalZone = isGloballyDragging && dragSource === "kanban";

  const { setNodeRef, isOver } = useDroppable({
    id: "sidebar-removal-zone",
    data: { type: "removal" },
    disabled: !showRemovalZone,
  });

  // The removal zone is an absolutely-positioned pad in the board's bottom-left
  // corner. Absolute is what keeps the promise that the panel never moves the
  // board: the zone appears mid-drag and disappears after, and an in-flow
  // element doing that would shove the columns sideways twice per drag.
  //
  // COMPACT, not a full-height strip, because the stage columns' droppables
  // span the whole board area and `rectIntersection` ranks candidates by
  // intersection-over-union, not by raw overlap or z-order. A 320x777 strip
  // scores a LOWER ratio than the 230x761 column underneath it for the same
  // dragged card, so a drop onto the visible red panel silently landed the
  // player in the first stage instead of removing it (measured on dev: the
  // card moved Negotiation -> Discovery). At 320x256 the pad's ratio beats
  // every column it overlaps, so the affordance and the drop agree.
  const removalZoneClassName = useMemo<string>(() => {
    const background = isOver ? "bg-status-error/50" : "bg-status-error/30";
    const border = isOver
      ? "border-2 border-solid border-status-error"
      : "border-0 border-solid border-transparent";
    return `absolute bottom-6 left-6 z-30 h-64 w-80 overflow-visible rounded-lg ${background} ${border}`;
  }, [isOver]);

  const tabBody = useMemo(() => {
    if (selectedSource === "suggested") {
      return (
        <SuggestedPlayersList
          kanbanBoardId={kanbanBoardId}
          addingPlayerIds={addingPlayerIds}
          onAdd={handleAdd}
        />
      );
    }
    return (
      <AddPlayersSourceList
        source={selectedSource}
        kanbanCards={kanbanCards}
        boardFilters={boardFilters}
        serverGpvRange={serverGpvRange}
        onAdd={handleAdd}
        addingPlayerIds={addingPlayerIds}
        onGlobalDragStart={onGlobalDragStart}
        onGlobalDragEnd={onGlobalDragEnd}
        onRemovePlayerFromBoard={onRemovePlayerFromBoard}
        onAutoScroll={onAutoScroll}
      />
    );
  }, [
    addingPlayerIds,
    boardFilters,
    handleAdd,
    kanbanBoardId,
    kanbanCards,
    onAutoScroll,
    onGlobalDragEnd,
    onGlobalDragStart,
    onRemovePlayerFromBoard,
    selectedSource,
    serverGpvRange,
  ]);

  /* SE-7094: the LISTS options — the org's watchlists, as chip options. */
  const listOptions = useMemo(
    () =>
      watchlistOptionsQuery.watchlists.map(watchlist => ({
        id: watchlist.id,
        label: watchlist.name,
      })),
    [watchlistOptionsQuery.watchlists]
  );

  /* The filters panel (PC3/PC4), mounted only while it owns the drawer. */
  const filterPanelBody = useMemo(
    () =>
      bodyMode === "filters" ? (
        <AddPlayersFilterPanel
          filterState={filterState}
          reasonSelection={appliedReasons}
          reasonOptions={reasonOptions}
          listSelection={appliedListIds}
          listOptions={listOptions}
          onCommitReasons={handleCommitReasons}
          onCommitLists={handleCommitLists}
          onClose={handleFiltersClose}
        />
      ) : null,
    [
      appliedListIds,
      appliedReasons,
      bodyMode,
      filterState,
      handleCommitLists,
      handleCommitReasons,
      handleFiltersClose,
      listOptions,
      reasonOptions,
    ]
  );

  /* The filtered results (PC7), mounted only while filters are committed. */
  const filteredResultsBody = useMemo(
    () =>
      bodyMode === "filtered" ? (
        <AddPlayersFilteredResults
          filterState={filterState}
          reasonSelection={appliedReasons}
          listSelection={appliedListIds}
          listOptions={listOptions}
          onRemoveReason={handleRemoveReason}
          onRemoveList={handleRemoveList}
          onClearAll={handleClearAllFilters}
          candidates={suggestions.candidates}
          allWatchlistIds={allWatchlistIds}
          serverGpvRange={serverGpvRange}
          pipelinePlayerIds={pipelinePlayerIds}
          addingPlayerIds={addingPlayerIds}
          onAdd={handleAdd}
        />
      ) : null,
    [
      addingPlayerIds,
      allWatchlistIds,
      appliedListIds,
      appliedReasons,
      bodyMode,
      filterState,
      handleAdd,
      handleClearAllFilters,
      handleRemoveList,
      handleRemoveReason,
      listOptions,
      pipelinePlayerIds,
      serverGpvRange,
      suggestions.candidates,
    ]
  );

  return (
    <AddPlayersPanelView
      setNodeRef={setNodeRef}
      removalZoneClassName={removalZoneClassName}
      showRemovalZone={showRemovalZone}
      isOpen={isOpen}
      onClose={handleClose}
      isMobile={isMobile}
      boardName={boardName ?? "Pipeline"}
      bodyMode={bodyMode}
      sources={sources}
      selectedSource={selectedSource}
      sourceHandlers={sourceHandlers}
      tabCounts={tabCounts}
      isFiltered={hasAppliedFilters}
      onFiltersOpen={handleFiltersOpen}
      searchQuery={search.searchQuery}
      onSearchChange={search.onSearchChange}
      onSearchClear={search.onSearchClear}
      searchPlayers={search.players}
      onSearchLoadMore={search.loadMore}
      searchHasMore={search.hasMore}
      searchTotalCount={search.totalCount}
      isSearchLoading={search.isLoading}
      isSearchEmpty={search.isEmpty}
      searchError={search.error}
      pipelinePlayerIds={pipelinePlayerIds}
      addingPlayerIds={addingPlayerIds}
      onAdd={handleAdd}
      tabBody={tabBody}
      filterPanelBody={filterPanelBody}
      filteredResultsBody={filteredResultsBody}
    />
  );
};

/**
 * Container: the key-reset wrapper.
 *
 * The panel's collapsed state and selected tab are MOUNT defaults derived from
 * the open pipeline. When the user switches pipelines those defaults must be
 * re-evaluated, and `react-hooks/set-state-in-effect` forbids doing that with a
 * reset effect. So the reset is a remount, keyed on the board id — the pattern
 * established for ConfirmDialog in SE-5524.
 *
 * Keying on the board id (and NOT on the card count) is what makes "collapsed
 * by default" mean "evaluated once per pipeline": adding a card to the open
 * pipeline leaves the panel exactly as the user left it.
 *
 * SE-6488 extends the key with the header's expand-request counter. The board
 * header's `+ Add player` primary CTA bumps that counter, which remounts this
 * rail with `startExpanded`, so the CTA opens the rail without a
 * reset-on-prop-change effect. Because the counter only ever changes on an
 * explicit press, the "evaluated once per pipeline" property above still holds
 * for every other render.
 *
 * SE-6945 also makes the panel the OWNER of the drawer's committed filter
 * state — see the stateful component above.
 * @param props - Panel props; `kanbanBoardId` doubles as the reset key.
 * @returns The keyed stateful panel.
 */
const AddPlayersPanelContainer = (props: AddPlayersPanelContainerProps) => {
  const { shouldStartExpanded, expandKey } = useAddPlayersPanelState();

  return (
    <AddPlayersPanelStateful
      key={`${props.kanbanBoardId ?? "no-board"}:${expandKey}`}
      {...props}
      startExpanded={shouldStartExpanded(props.kanbanBoardId)}
    />
  );
};

AddPlayersPanelContainer.displayName = "AddPlayersPanelContainer";

export default AddPlayersPanelContainer;
